/**
 * ContextPills Component
 *
 * Displays selected agents as removable pills/badges above the input.
 * Features:
 * - Remove button (X) on each pill
 * - Horizontal scrollable layout
 * - Responsive design
 */

import { Bot, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { AgentContextItem } from "@/types/agent-context"
import { cn } from "@/lib/utils"

type ContextPillsProps = {
  items: AgentContextItem[]
  onRemove?: (id: string) => void
  className?: string
  readOnly?: boolean
}

/**
 * ContextPills component for displaying selected agents
 */
export function ContextPills({
  items,
  onRemove,
  className,
  readOnly = false,
}: ContextPillsProps) {
  if (items.length === 0) {
    return null
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 pb-2", className)}
      data-testid="context-pills"
    >
      {items.map((item) => (
        <Badge
          className={cn(
            "flex items-center gap-1.5 py-1 font-medium text-xs transition-colors rounded-lg",
            readOnly ? "pr-2 pl-2" : "pr-1 pl-2",
            "bg-primary/10 text-primary hover:bg-primary/20 dark:bg-primary/20 dark:text-primary dark:hover:bg-primary/30"
          )}
          key={item.id}
          variant="secondary"
        >
          <Bot className="h-3 w-3" />
          <span className="max-w-[200px] truncate">{item.name}</span>
          {item.icon && <span className="text-sm">{item.icon}</span>}
          {!readOnly && onRemove && (
            <Button
              aria-label={`Remove ${item.name}`}
              className="ml-0.5 h-4 w-4 rounded-full p-0 hover:bg-black/10 dark:hover:bg-white/10"
              onClick={(e) => {
                e.preventDefault()
                onRemove(item.id)
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </Badge>
      ))}
    </div>
  )
}
